/**
 * Expanded detail for calculated insight / pattern feed events.
 *
 * This is a thin router: it decides whether a payload carries structured detail
 * worth expanding (`hasInsightDetail`) and delegates rendering to the insight
 * view registry. High-value types (portfolio funnel, volume / sales-person
 * trend, disruption, capture rate, pricing-source trend, lane trend, newly
 * opened) have bespoke, human-readable layouts; everything else renders through
 * a shared generic view so new types display without a dedicated component.
 */
import type { FeedEffectPayload } from "@/types/feed";

import { isFriendlyMetricKey } from "./insights/formatInsight";
import { renderInsightBody } from "./insights/registry";

type Rec = Record<string, unknown>;

function isInsightOrPattern(payload: FeedEffectPayload): boolean {
  const kind = (payload as { kind?: string })?.kind;
  return kind === "insight" || kind === "pattern";
}

/** A report-sourced alert (validation breach / OCF mismatch) with numbers. */
function isReportAlert(payload: FeedEffectPayload): boolean {
  const p = payload as unknown as Rec;
  if (p.kind !== "alert") return false;
  return Object.entries(p).some(
    ([key, value]) => typeof value === "number" && isFriendlyMetricKey(key),
  );
}

/** Whether a record carries a metrics object or a non-empty top_* / by_* array. */
function hasStructuredDetail(p: Rec): boolean {
  if (p.metrics && typeof p.metrics === "object") return true;
  return Object.keys(p).some(
    (k) =>
      (k.startsWith("top_") || k.startsWith("by_")) &&
      Array.isArray(p[k]) &&
      (p[k] as unknown[]).length > 0,
  );
}

/** True when the payload carries structured detail worth expanding. */
export function hasInsightDetail(payload: FeedEffectPayload): boolean {
  if (isReportAlert(payload)) return true;
  if (!isInsightOrPattern(payload)) return false;
  const p = payload as unknown as Rec;
  if (hasStructuredDetail(p)) return true;
  // Legacy insights (daily booking summary, weekly trend) nest their metrics
  // and breakdowns under `data` rather than at the top level.
  const data = p.data;
  if (data && typeof data === "object") {
    const d = data as Rec;
    if (hasStructuredDetail(d)) return true;
    // Weekly trend has flat numeric fields under `data`, no arrays.
    if (typeof d.current_teu === "number" || typeof d.total_bookings === "number") {
      return true;
    }
  }
  return false;
}

export function FeedInsightDetail({
  payload,
  eventId,
}: {
  payload: FeedEffectPayload;
  /** Needed by drill-downs that lazily fetch detail (OCF reconciliation). */
  eventId?: string;
}) {
  if (!isInsightOrPattern(payload) && !isReportAlert(payload)) return null;
  return <>{renderInsightBody(payload as unknown as Rec, eventId)}</>;
}
